/* eslint-disable camelcase */
import type { AuthContextUser, CoopDashUser, RetsDashUser } from 'contexts';
import { AuthContext, ExternalAuthContext } from 'contexts';
import mixpanel from 'mixpanel-browser';
import type { Dict } from 'mixpanel-browser';
import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DailySessionStorage, featureEnabled } from 'utils';
import { PlanName } from '@bb/automaton';
import type { MixpanelPage, MixpanelEvent } from './events';

const token = process.env.MIXPANEL_PROJECT_TOKEN;

if (token) {
  mixpanel.init(token, {
    debug: process.env.NODE_ENV === 'development',
    track_pageview: false,
    persistence: 'localStorage',
  });
}

const storage = new DailySessionStorage('mixpanel_daily_session_expiry');

type UseMixpanelProps = {
  pageTracking?: PageTracking;
  trackDailySession?: boolean;
  auth?: AuthContextUser;
};

type PageTracking = { pageName: MixpanelPage; properties?: Dict };

type UseMixpanelHandler = {
  trackEvent: (eventName: MixpanelEvent, properties?: Dict) => void;
};

const useMixpanel = ({
  pageTracking,
  trackDailySession,
  auth: authFromProps,
}: UseMixpanelProps): UseMixpanelHandler => {
  const location = useLocation();
  const { auth: authFromContext } = useContext(AuthContext);
  const { auth: externalAuth } = useContext(ExternalAuthContext);
  const mlsForExternalUser = (externalAuth?.user as CoopDashUser | RetsDashUser)
    ?.mls;
  const auth = authFromContext || authFromProps;
  const uniqueExternalId =
    externalAuth?.uid &&
    mlsForExternalUser &&
    `${mlsForExternalUser}-${externalAuth.uid}`;
  const authId = auth?.id ?? uniqueExternalId;
  const mixpanelEnabled =
    (auth || mlsForExternalUser) && token && featureEnabled('mixpanelFeature');

  const { assumingUserId, isAssumedNotBB, assumerIdMlsStaff } = auth || {};
  const isAssumed = Boolean(
    assumingUserId || isAssumedNotBB || assumerIdMlsStaff,
  );

  const updateUserProfile = () => {
    if (auth) {
      mixpanel.people.set({
        $user_id: authId,
        $first_name: auth.firstName,
        $last_name: auth.lastName,
        Brokerage: auth.org.name,
        Association: auth.mls_numbers[0]?.board,
        MLS: auth.org.mlsTypes?.[0],
        Role: auth.role,
        PlanType: auth.org.onboardingPortal?.plan || PlanName.BROKERAGE,
      });
    } else if (mlsForExternalUser) {
      mixpanel.people.set({
        $user_id: authId,
        $first_name: externalAuth?.user?.firstName,
        MLS: (externalAuth?.user as CoopDashUser | RetsDashUser)?.mls,
      });
    }
  };

  const trackPage = ({ pageName, properties }: PageTracking) => {
    if (auth || mlsForExternalUser) {
      if (!mixpanel.people) return;
      mixpanel.identify(authId);

      mixpanel.track_pageview({
        ...properties,
        'Page Name': pageName,
        isAssumed,
        mls: mlsForExternalUser,
      });
    }
  };

  const trackEvent = (eventName: MixpanelEvent, properties?: Dict) => {
    if (auth || mlsForExternalUser) {
      if (!mixpanel.people) return;
      mixpanel.identify(authId);

      mixpanel.track(eventName, {
        ...properties,
        isAssumed,
        mls: mlsForExternalUser,
      });
    }
  };

  useEffect(() => {
    if (pageTracking && mixpanelEnabled) {
      trackPage(pageTracking);
    }
    /**
     * Pass location as a dependency to keep track of search/query changes within the route
     */
  }, [mixpanelEnabled, location, pageTracking?.pageName]);

  useEffect(() => {
    if (trackDailySession && mixpanelEnabled) {
      if (storage.authorize()) {
        /** KUSA-1051 Temporarily disable daily session tracking
        trackEvent(MixpanelEvent.DailySession);
        */
        updateUserProfile();
      }
    }
    /**
     * Pass location as a dependency to keep track of search/query changes within the route
     */
  }, [mixpanelEnabled, location, trackDailySession]);

  return { trackEvent };
};

export default useMixpanel;
